'use client'

import Link from 'next/link'
import { trackEvent } from '@/utils/analytics'

interface DefenseCrossSellProps {
  /** Product slug, sent as the `product` property on the PostHog event (product pages). */
  product?: string
  /** Pricing segment context (e.g. 'gcc-high'), extra event context. */
  segment?: string
  /** Category slug when rendered on a category page, extra event context. */
  category?: string
  /**
   * Show the one-line "GCC High is the safer default" nudge. Defaults to
   * segment === 'gcc' (GCC, non-High, surfaces). Product overview pages pass
   * this explicitly since their segment prop is the product's government
   * segment (GCC High when present), while the page's quote context can
   * still be GCC.
   */
  suggestGccHigh?: boolean
}

/**
 * CMMC / Secureframe Defense cross-sell module (MAR-994).
 *
 * Shown on marketplace surfaces where the visitor is browsing GCC or GCC High
 * SKUs (defense ICP) to convert licensing traffic into Secureframe Defense
 * demand. Rendered below the pricing/segments content and above the FAQ so it
 * never interrupts the purchase flow. CTA links to /cmmc, the canonical
 * Secureframe Defense page.
 */
export default function DefenseCrossSell({ product, segment, category, suggestGccHigh }: DefenseCrossSellProps) {
  const handleClick = () => {
    trackEvent('defense_cross_sell_clicked', {
      ...(product ? { product } : {}),
      ...(segment ? { segment } : {}),
      ...(category ? { category } : {}),
    })
  }

  const showGccHighNote = suggestGccHigh ?? segment === 'gcc'
  const headline = segment === 'gcc-high' ? 'Buying GCC High for CMMC Level 2?' : 'Working toward CMMC Level 2?'

  return (
    <div className="relative isolate overflow-hidden rounded-2xl bg-primary-ink-100 px-6 py-8 text-white sm:px-10 sm:py-10">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
        <div className="min-w-0">
          <p className="mb-2 text-[13px] font-medium uppercase tracking-[0.5px] text-primary-green-85">
            Secureframe Defense
          </p>
          <h3 className="text-2xl font-medium tracking-[-0.3px] text-white sm:text-[28px] sm:leading-9">{headline}</h3>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-white/75">
            The tenant is step one. Secureframe Defense collects evidence from your Microsoft environment, tracks all
            110 NIST SP 800-171 controls, and preps you for your C3PAO assessment.
          </p>
          {showGccHighNote && (
            <p className="mt-2 max-w-xl text-base leading-relaxed text-white/75">
              Handling CUI? GCC High is the safer default for defense contractors. GCC cannot hold ITAR or
              export-controlled data.
            </p>
          )}
        </div>
        <Link
          href="/cmmc"
          onClick={handleClick}
          className="inline-flex h-12 w-full shrink-0 items-center justify-center whitespace-nowrap rounded-lg bg-primary-green-85 px-5 text-[15px] font-medium text-primary-ink-100 no-underline transition-colors hover:bg-primary-green-150 sm:h-11 sm:w-auto sm:rounded-md sm:text-sm">
          See Secureframe Defense
        </Link>
      </div>
    </div>
  )
}
